<script setup lang="ts">
  import { cn } from "@/lib/utils"
  import { type HTMLAttributes } from "vue"
  import AlertClose from "@/components/ui/alert/AlertClose.vue"
  import AlertTitle from "@/components/ui/alert/AlertTitle.vue"
  import AlertToggle from "@/components/ui/alert/AlertToggle.vue"
  import {
    CollapsibleContent,
    CollapsibleRoot,
    CollapsibleTrigger,
  } from "@/components/ui/collapsible"
  import Icon from "@/components/ui/icons/Icon.vue"
  import { iconsMap } from "@/components/ui/icons/iconsMap.ts"

  import { alertVariants, type AlertVariants } from "."

  const props = defineProps<{
    class?: HTMLAttributes["class"]
    variant: AlertVariants["variant"]
    showCloseButton?: boolean
    title?: string
    collapsible?: boolean
    toggleLabel?: string
    defaultOpen?: boolean
    role?: "alert" | "status"
  }>()

  const iconVariant: Record<string, keyof typeof iconsMap> = {
    success: "CircleCheck",
    info: "Info",
    warning: "Warning",
    error: "Error",
  }
  const emit = defineEmits<(e: "closeButtonClicked") => void>()
</script>

<template>
  <component
    :is="props.collapsible ? CollapsibleRoot : 'div'"
    :default-open="props.collapsible ? (props.defaultOpen ?? false) : undefined"
    :class="
      cn(
        alertVariants({ variant }),
        props.collapsible && 'flex-wrap',
        props.class,
      )
    "
    :role="props.role ?? 'alert'"
  >
    <div
      class="flex gap-3 text-left"
      :class="props.collapsible ? 'min-w-0 flex-1' : 'w-full'"
    >
      <div
        class="flex items-start"
        :class="{ 'pt-[4px]': title, 'sm:pt-[2px]': !title }"
      >
        <Icon
          :name="iconVariant[variant!]"
          size="lg"
          :data-testid="`alert-icon-${variant}`"
        />
      </div>
      <div class="grid flex-1 grid-cols-[1fr_auto]" :class="{ 'gap-1': title }">
        <div class="items-center-center flex" data-testid="alert-content">
          <AlertTitle v-if="props.title">{{ props.title }}</AlertTitle>
          <template v-else><slot /></template>
        </div>
        <div class="inline-flex pl-3">
          <AlertClose
            v-if="props.showCloseButton && !props.collapsible"
            data-dd-action-name="Dismiss alert"
            @close="emit('closeButtonClicked')"
          />
        </div>
        <div class="col-span-2"><slot v-if="props.title" /></div>
      </div>
    </div>

    <slot v-if="!props.collapsible" name="action" />

    <CollapsibleTrigger v-if="props.collapsible" as-child>
      <AlertToggle
        class="mt-3 self-end sm:mt-0 sm:self-center"
        :label="props.toggleLabel ?? ''"
      />
    </CollapsibleTrigger>

    <CollapsibleContent
      v-if="props.collapsible"
      class="mt-3 basis-full overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down sm:mt-4"
      data-testid="alert-collapsible-content"
    >
      <slot name="collapsible" />
    </CollapsibleContent>
  </component>
</template>
